import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  ArrowRight,
  Users,
  Award,
  Play,
  CheckCircle,
  Briefcase,
  Building,
  Heart,
  Scale,
  Factory,
  BookOpen,
  Star,
  Sparkles,
  TrendingUp,
  Shield,
  Zap,
  Globe,
  ChevronLeft,
  ChevronRight,
  Search,
} from 'lucide-react';
import { webinarsAPI, speakersAPI, industriesAPI, statsAPI } from '@/services/api';
import { useNavigate } from 'react-router-dom';
import { WebinarCard } from '@/components/common/WebinarCard';
import SEO from '@/components/common/SEO';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Helper to resolve image URLs (handles both absolute and relative paths)
const resolveImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `${API_URL}${url}`;
};

const industryIcons = {
  'Briefcase': Briefcase,
  'Users': Users,
  'Microscope': Award,
  'Heart': Heart,
  'Building': Building,
  'BookOpen': BookOpen,
  'Factory': Factory,
  'HardHat': Building,
  'Scale': Scale,
  'Home': Building,
};

export default function HomePage() {
  const [featuredWebinars, setFeaturedWebinars] = useState([]);
  const [speakers, setSpeakers] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIndustry, setSelectedIndustry] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [webinarsRes, speakersRes, industriesRes, statsRes] = await Promise.all([
        webinarsAPI.getFeatured(10),
        speakersAPI.getFeatured(),
        industriesAPI.getAll(),
        statsAPI.getPublic(),
      ]);
      setFeaturedWebinars(webinarsRes.data);
      setSpeakers(speakersRes.data);
      setIndustries(industriesRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const navigate = useNavigate();

  const handleQuickSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (searchQuery) params.set('search', searchQuery);
    if (selectedIndustry) params.set('industry', selectedIndustry);
    navigate(`/webinars?${params.toString()}`);
  };

  // Get parent industries only for quick filter
  const parentIndustries = industries.filter(ind => !ind.parent_id);

  return (
    <div className="min-h-screen overflow-hidden" data-testid="home-page">
      <SEO
        title="Governance, Risk & Compliance Webinars & Certifications"
        description="GRC Educators provides expert-led governance, risk, and compliance (GRC) webinars and CPE/CE certifications — live and on-demand training across healthcare, banking, HR, finance, IT security and 45+ regulated industries."
        path="/"
      />
      {/* ============== HERO SECTION (Mailcraft light + grey square-grid) ============== */}
      <section className="relative bg-white overflow-hidden">
        {/* light grey square-boxes background — uniform, edge to edge, no fade */}
        <div
          className="pointer-events-none absolute inset-0 z-0"
          style={{
            backgroundImage:
              'linear-gradient(to right, rgba(15,23,42,0.07) 1px, transparent 1px), linear-gradient(to bottom, rgba(15,23,42,0.07) 1px, transparent 1px)',
            backgroundSize: '46px 46px',
          }}
        />
        <div className="container-custom relative z-10 pt-28 md:pt-32 pb-24">
          <div className="grid lg:grid-cols-[1.05fr_0.95fr] gap-12 items-start">
            {/* Left content */}
            <div>
              <div className="mono-label inline-flex items-center gap-2 text-[11px] text-zinc-600 bg-white border border-zinc-200 rounded-full pl-2.5 pr-3.5 py-1.5 mb-6 animate-fade-in">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                Governance, Risk &amp; Compliance training across 45+ industries
              </div>

              <h1 className="font-heading text-4xl md:text-5xl lg:text-[3.6rem] font-bold mb-5 leading-[1.02] tracking-[-0.03em] animate-fade-in-up">
                <span className="text-zinc-950">Master governance, risk &amp; compliance </span>
                <span className="text-slate-400">across every regulated industry.</span>
              </h1>

              <p className="text-base text-zinc-500 mb-7 leading-relaxed animate-fade-in-up opacity-0 stagger-2 max-w-lg">
                Thousands of expert-led webinars spanning banking, healthcare, IT security, HR,
                life sciences, pharma, finance and 40+ more domains — practical, current, and
                delivered by industry authorities.
                <span className="font-semibold text-zinc-900"> One platform.</span>
              </p>

              <div className="flex flex-wrap gap-3 animate-fade-in-up opacity-0 stagger-3">
                <Link to="/webinars">
                  <Button
                    className="bg-zinc-950 hover:bg-zinc-800 text-white font-semibold px-6 py-5 text-sm rounded-lg transition-colors"
                    data-testid="explore-webinars-btn"
                  >
                    Explore Webinars
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
                <Link to="/register">
                  <Button
                    variant="outline"
                    className="border border-zinc-300 bg-white text-zinc-900 hover:bg-zinc-50 font-semibold px-6 py-5 text-sm rounded-lg transition-colors"
                    data-testid="get-started-btn"
                  >
                    Get Started Free
                  </Button>
                </Link>
              </div>

              {/* stats moved below (full-width row) */}
            </div>

            {/* Right - white dashboard mockup card (MailCraft signature) */}
            <div className="hidden lg:block animate-scale-in">
              <div className="bg-white border border-zinc-200 rounded-2xl shadow-[0_24px_70px_-24px_rgba(15,23,42,0.22)] overflow-hidden">
                {/* Window bar */}
                <div className="flex items-center gap-2 px-5 py-3 border-b border-zinc-100">
                  <span className="w-2.5 h-2.5 rounded-full bg-zinc-200" />
                  <span className="w-2.5 h-2.5 rounded-full bg-zinc-200" />
                  <span className="w-2.5 h-2.5 rounded-full bg-zinc-200" />
                  <span className="mono-label text-[10px] text-slate-400 ml-3">grceducators / webinars / live</span>
                </div>

                <div className="p-5">
                  {/* Stat cells */}
                  <div className="grid grid-cols-3 border border-zinc-100 rounded-xl overflow-hidden mb-4">
                    <div className="p-4 border-r border-zinc-100">
                      <div className="mono-label text-[9px] text-slate-400 mb-1.5">ENROLLED</div>
                      <div className="font-heading text-xl font-bold text-zinc-950">12,480</div>
                    </div>
                    <div className="p-4 border-r border-zinc-100">
                      <div className="mono-label text-[9px] text-slate-400 mb-1.5">CPE ISSUED</div>
                      <div className="font-heading text-xl font-bold text-emerald-600">8,340</div>
                    </div>
                    <div className="p-4">
                      <div className="mono-label text-[9px] text-slate-400 mb-1.5">RATING</div>
                      <div className="font-heading text-xl font-bold text-slate-500">4.9</div>
                    </div>
                  </div>

                  {/* Timeline chart */}
                  <div className="border border-zinc-100 rounded-xl p-4 mb-4">
                    <div className="mono-label text-[9px] text-slate-400 mb-3">REGISTRATIONS · LAST 30 DAYS</div>
                    <svg viewBox="0 0 320 70" className="w-full h-16" preserveAspectRatio="none">
                      <polyline
                        fill="none"
                        stroke="#0b0b0f"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        points="0,58 32,52 64,55 96,44 128,47 160,36 192,39 224,26 256,30 288,18 320,12"
                      />
                      <polyline
                        fill="rgba(16,185,129,0.06)"
                        stroke="none"
                        points="0,70 0,58 32,52 64,55 96,44 128,47 160,36 192,39 224,26 256,30 288,18 320,12 320,70"
                      />
                    </svg>
                  </div>

                  {/* Status rows */}
                  {[
                    { label: 'Live session · IT & PCI Compliance', status: 'live', color: 'text-emerald-600', dot: 'bg-emerald-500' },
                    { label: 'Recording · HIPAA Fundamentals', status: 'ready', color: 'text-slate-500', dot: 'bg-slate-400' },
                    { label: 'CPE credits · HR Compliance', status: 'approved', color: 'text-emerald-600', dot: 'bg-emerald-500' },
                    { label: 'Certificate · Risk Management', status: 'issued', color: 'text-slate-400', dot: 'bg-slate-300' },
                  ].map((row, idx) => (
                    <div key={idx} className={`flex items-center justify-between py-2.5 ${idx !== 3 ? 'border-b border-zinc-50' : ''}`}>
                      <span className="mono-label text-[10px] text-zinc-600 truncate pr-3">{row.label}</span>
                      <span className={`mono-label inline-flex items-center gap-1.5 text-[10px] ${row.color} shrink-0`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${row.dot}`} />
                        {row.status}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Stats — full-width single row with light icons */}
          <div className="mt-16 pt-10 border-t border-zinc-200 grid grid-cols-2 md:grid-cols-4 gap-y-8 animate-fade-in-up opacity-0 stagger-4">
            {[
              { icon: Play, value: stats.total_webinars, fallback: 1121, label: 'Webinars', tile: 'bg-emerald-50 border-emerald-100', ic: 'text-emerald-600' },
              { icon: Users, value: stats.total_speakers, fallback: 726, label: 'Speakers', tile: 'bg-sky-50 border-sky-100', ic: 'text-sky-600' },
              { icon: Award, value: stats.total_professionals, fallback: 4045, label: 'Certified', tile: 'bg-amber-50 border-amber-100', ic: 'text-amber-600' },
              { icon: Globe, value: stats.total_industries, fallback: 45, label: 'Industries', tile: 'bg-indigo-50 border-indigo-100', ic: 'text-indigo-600' },
            ].map((stat, idx) => {
              const Icon = stat.icon;
              const num = (stat.value ?? stat.fallback).toLocaleString();
              return (
                <div
                  key={idx}
                  className={`flex items-center gap-4 px-2 md:px-6 ${idx !== 3 ? 'md:border-r md:border-zinc-200' : ''}`}
                  data-testid={`hero-stat-${stat.label.toLowerCase()}`}
                >
                  <div className={`w-12 h-12 rounded-xl border flex items-center justify-center shrink-0 ${stat.tile}`}>
                    <Icon className={`w-5 h-5 ${stat.ic}`} />
                  </div>
                  <div>
                    <div className="font-heading text-2xl md:text-3xl font-bold text-zinc-950 tracking-tight leading-none">
                      {num}
                    </div>
                    <div className="mono-label text-[10px] text-slate-400 mt-1.5">{stat.label}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============== FEATURED WEBINARS ============== */}
      <section className="py-24 bg-transparent overflow-hidden" data-testid="featured-webinars">
        <div className="container-custom">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
            <div>
              <span className="mono-label inline-flex items-center gap-2 text-[11px] text-zinc-600 bg-white border border-zinc-200 rounded-full px-3 py-1 mb-5">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                Featured Events
              </span>
              <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight">
                <span className="text-zinc-950">Upcoming </span>
                <span className="text-slate-400">webinars.</span>
              </h2>
              <p className="text-zinc-500 mt-4 text-base max-w-xl">
                Join our expert-led sessions and stay ahead in compliance.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button 
                onClick={() => {
                  const container = document.getElementById('webinar-carousel');
                  if (container) container.scrollBy({ left: -400, behavior: 'smooth' });
                }}
                className="w-10 h-10 rounded-full bg-white border border-zinc-200 flex items-center justify-center hover:bg-zinc-50 hover:border-zinc-300 transition-colors"
                data-testid="carousel-prev"
                aria-label="Previous webinars"
              >
                <ChevronLeft className="w-5 h-5 text-zinc-600" />
              </button>
              <button 
                onClick={() => {
                  const container = document.getElementById('webinar-carousel');
                  if (container) container.scrollBy({ left: 400, behavior: 'smooth' });
                }}
                className="w-10 h-10 rounded-full bg-white border border-zinc-200 flex items-center justify-center hover:bg-zinc-50 hover:border-zinc-300 transition-colors"
                data-testid="carousel-next"
                aria-label="Next webinars"
              >
                <ChevronRight className="w-5 h-5 text-zinc-600" />
              </button>
              <Link to="/webinars">
                <Button className="bg-zinc-950 hover:bg-zinc-800 text-white rounded-lg px-5 py-2 text-sm">
                  View All
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>

          {/* Upcoming webinars — 3 per row, scrollable */}
          <div 
            id="webinar-carousel"
            className="flex gap-6 overflow-x-auto pb-4 scroll-smooth snap-x snap-mandatory scrollbar-hide"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {featuredWebinars.slice(0, 9).map((webinar, idx) => (
              <div 
                key={webinar.id} 
                className="flex-shrink-0 w-[calc(33.333%-16px)] min-w-[320px] snap-start"
              >
                <WebinarCard 
                  item={webinar} 
                  type="webinar" 
                  animationDelay={idx * 0.05}
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============== INDUSTRIES SECTION ============== */}
      <section className="py-24 bg-white border-y border-zinc-200" data-testid="industries-section">
        <div className="container-custom relative z-10">
          <div className="text-center mb-16">
            <div className="mono-label inline-flex items-center gap-2 text-xs text-zinc-500 bg-zinc-100 border border-zinc-200 rounded-full px-3 py-1 mb-4">
              <span className="w-1.5 h-1.5 rounded-full bg-gold-500" />
              Industries We Serve
            </div>
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-zinc-900 mb-4 tracking-tight">
              Compliance training across industries
            </h2>
            <p className="text-zinc-600 max-w-2xl mx-auto text-lg">
              Specialized GRC education tailored to your industry's unique regulatory requirements.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {industries.slice(0, 10).map((industry, idx) => {
              const IconComponent = industryIcons[industry.icon] || Briefcase;
              return (
                <Link key={industry.id} to={`/industries/${industry.slug || industry.id}`}>
                  <div
                    className="group p-6 text-center bg-white border border-zinc-200 rounded-xl hover:border-zinc-900 transition-colors duration-200 cursor-pointer animate-scale-in opacity-0"
                    style={{ animationDelay: `${idx * 0.05}s` }}
                    data-testid={`industry-card-${industry.id}`}
                  >
                    <div className="w-14 h-14 mx-auto mb-4 rounded-xl bg-zinc-100 border border-zinc-200 flex items-center justify-center group-hover:bg-navy-50 group-hover:border-navy-200 transition-colors">
                      <IconComponent className="w-7 h-7 text-navy-600" />
                    </div>
                    <h3 className="font-semibold text-zinc-900 text-sm line-clamp-2 group-hover:text-navy-700 transition-colors">
                      {industry.name}
                    </h3>
                  </div>
                </Link>
              );
            })}
          </div>

          <div className="text-center mt-12">
            <Link to="/industries">
              <Button className="bg-zinc-900 text-white hover:bg-zinc-800 rounded-md px-8 font-semibold">
                View All Industries
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* ============== WHY CHOOSE US — MailCraft dark section ============== */}
      <section className="py-24 bg-[#0a0e14] border-t border-zinc-800">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="mono-label inline-flex items-center gap-2 text-[11px] text-emerald-400 mb-6">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                // COMPLIANCE.SUITE
              </span>
              <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-[-0.03em] leading-[1.02] mb-6">
                <span className="text-white">Verified credits, expert delivery, </span>
                <span className="text-zinc-500">zero guesswork.</span>
              </h2>
              <p className="text-zinc-400 text-lg mb-10 leading-relaxed max-w-lg">
                Every session is built to keep you audit-ready — accredited instructors,
                tracked CPE/CE credits, and instant certificates.
              </p>

              <div className="space-y-4">
                {[
                  'Expert instructors with 20+ years of experience',
                  'CPE/CE credits recognized by certification bodies',
                  'Live interactive sessions with real-time Q&A',
                  'On-demand library for flexible learning',
                  'Certificates of completion for all webinars',
                ].map((text, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-400 shrink-0" />
                    <span className="text-zinc-300">{text}</span>
                  </div>
                ))}
              </div>

              <div className="mt-10">
                <Link to="/about">
                  <Button className="bg-white hover:bg-zinc-200 text-zinc-950 font-semibold rounded-lg px-6 transition-colors">
                    Learn More About Us
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
              </div>
            </div>

            {/* Training image with floating stat badge (Mailcraft dark theme) */}
            <div className="relative">
              <div className="rounded-2xl overflow-hidden border border-zinc-800 shadow-2xl">
                <img
                  src="https://images.pexels.com/photos/7644016/pexels-photo-7644016.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                  alt="Expert-led compliance training session"
                  className="w-full h-[420px] object-cover"
                  loading="lazy"
                />
              </div>
              {/* floating badge card */}
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-2xl px-6 py-5 flex items-center gap-4 border border-zinc-100">
                <div className="w-12 h-12 rounded-xl bg-zinc-950 flex items-center justify-center shrink-0">
                  <Award className="w-6 h-6 text-emerald-400" />
                </div>
                <div>
                  <div className="font-heading text-2xl font-bold text-zinc-950 leading-none">15K+</div>
                  <div className="text-sm text-slate-400 mt-1">Certified Professionals</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============== SPEAKERS SECTION ============== */}
      <section className="py-24 bg-white border-t border-zinc-200" data-testid="speakers-section">
        <div className="container-custom">
          <div className="flex items-center justify-between mb-12">
            <div>
              <span className="mono-label inline-flex items-center gap-2 text-[11px] text-zinc-600 bg-white border border-zinc-200 rounded-full px-3 py-1 mb-5">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                Our Expert Speakers
              </span>
              <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight mb-4">
                <span className="text-zinc-950">Learn from </span>
                <span className="text-slate-400">industry leaders.</span>
              </h2>
              <p className="text-zinc-500 max-w-2xl text-lg">
                Our speakers bring decades of real-world experience in regulatory compliance.
              </p>
            </div>
            {/* Navigation Arrows */}
            <div className="hidden md:flex items-center gap-2">
              <button
                onClick={() => {
                  const container = document.getElementById('speakers-carousel');
                  if (container) container.scrollBy({ left: -400, behavior: 'smooth' });
                }}
                className="w-12 h-12 rounded-full bg-white border border-slate-200 hover:border-gold-400 hover:bg-gold-50 flex items-center justify-center transition-all shadow-sm"
                aria-label="Previous speakers"
              >
                <ChevronLeft className="w-5 h-5 text-navy-600" />
              </button>
              <button
                onClick={() => {
                  const container = document.getElementById('speakers-carousel');
                  if (container) container.scrollBy({ left: 400, behavior: 'smooth' });
                }}
                className="w-12 h-12 rounded-full bg-white border border-slate-200 hover:border-gold-400 hover:bg-gold-50 flex items-center justify-center transition-all shadow-sm"
                aria-label="Next speakers"
              >
                <ChevronRight className="w-5 h-5 text-navy-600" />
              </button>
            </div>
          </div>

          {/* Speaker Carousel - 3 cards per row within container */}
          <div 
            id="speakers-carousel"
            className="flex gap-6 overflow-x-auto pb-4 scrollbar-hide snap-x snap-mandatory scroll-smooth"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {speakers.slice(0, 9).map((speaker, idx) => (
              <Link 
                key={speaker.id} 
                to={`/speakers/${speaker.slug || speaker.id}`}
                className="flex-shrink-0 w-[calc(33.333%-16px)] min-w-[360px] snap-start"
              >
                <Card
                  className="group card-elevated text-center p-8 h-full animate-fade-in opacity-0"
                  style={{ animationDelay: `${idx * 0.1}s` }}
                  data-testid={`speaker-card-${speaker.id}`}
                >
                  <div className="relative inline-block mb-5">
                    <div className="w-32 h-32 rounded-2xl overflow-hidden mx-auto ring-4 ring-slate-100 group-hover:ring-gold-200 transition-all">
                      <img
                        src={resolveImageUrl(speaker.profile_image) || 'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=400&q=80'}
                        alt={`${speaker.first_name} ${speaker.last_name}`}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      />
                    </div>
                    <div className="absolute -bottom-2 -right-2 w-9 h-9 bg-gold-500 rounded-lg flex items-center justify-center shadow-lg">
                      <Star className="w-5 h-5 text-navy-800 fill-navy-800" />
                    </div>
                  </div>
                  
                  <h3 className="font-heading text-2xl font-bold text-navy-700 mb-2 group-hover:text-gold-600 transition-colors">
                    {speaker.first_name} {speaker.last_name}
                  </h3>
                  <p className="text-gold-600 font-medium text-sm mb-3 line-clamp-1">
                    {speaker.expertise_areas?.slice(0, 2).join(' • ') || 'GRC Expert'}
                  </p>
                  <p className="text-slate-600 text-sm line-clamp-2 mb-5">
                    {speaker.headline || speaker.short_bio || speaker.bio?.substring(0, 100)}
                  </p>
                  
                  <div className="flex items-center justify-center gap-6 pt-5 border-t border-slate-100">
                    <div className="text-center">
                      <p className="text-xl font-bold text-navy-700">{speaker.years_experience || 15}+</p>
                      <p className="text-xs text-slate-500">Years</p>
                    </div>
                    <div className="w-px h-10 bg-slate-200" />
                    <div className="text-center">
                      <p className="text-xl font-bold text-navy-700 flex items-center gap-1">
                        <Star className="w-4 h-4 text-gold-500 fill-gold-500" />
                        {speaker.rating?.toFixed(1) || '4.9'}
                      </p>
                      <p className="text-xs text-slate-500">Rating</p>
                    </div>
                    <div className="w-px h-10 bg-slate-200" />
                    <div className="text-center">
                      <p className="text-xl font-bold text-navy-700">{speaker.total_webinars || 0}</p>
                      <p className="text-xs text-slate-500">Webinars</p>
                    </div>
                  </div>
                </Card>
              </Link>
            ))}
          </div>

        </div>
      </section>

      {/* ============== CTA SECTION ============== */}
      <section className="py-24 bg-white border-t border-zinc-200 relative overflow-hidden">
        <div className="container-custom relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="mono-label inline-flex items-center gap-2 text-xs text-zinc-500 bg-zinc-100 border border-zinc-200 rounded-full px-3 py-1 mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-gold-500" />
              Start Your Journey Today
            </div>
            
            <h2 className="font-heading text-4xl md:text-6xl font-bold tracking-tight mb-6">
              <span className="text-zinc-900">Ready to advance </span>
              <span className="text-zinc-400">your career?</span>
            </h2>
            <p className="text-zinc-600 text-xl mb-10 max-w-2xl mx-auto">
              Join thousands of professionals who trust GRC Educators for their 
              compliance training needs.
            </p>
            
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/register">
                <Button
                  size="lg"
                  className="bg-zinc-900 hover:bg-zinc-800 text-white font-semibold px-10 py-7 text-lg rounded-md transition-colors"
                >
                  Create Free Account
                </Button>
              </Link>
              <Link to="/webinars">
                <Button
                  size="lg"
                  variant="outline"
                  className="border border-zinc-300 bg-white text-zinc-900 hover:bg-zinc-50 font-semibold px-10 py-7 text-lg rounded-md transition-colors"
                >
                  Browse Webinars
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
